import { useMutation } from '@tanstack/react-query';
import { updateDataFlow } from '@/features/adp/services/api-service';
import type {
  UpdateDataFlowRequest,
  UpdateDataFlowResponse,
} from '@/features/adp/services/api-service.types';
import { toast } from 'sonner';

export type UpdateDataFlowMutationVariables = {
  id: string;
  payload: UpdateDataFlowRequest;
};

type UseUpdateDataFlowMutationOptions = {
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
  onSuccess?: (data: UpdateDataFlowResponse, variables: UpdateDataFlowMutationVariables) => void;
  onError?: (error: Error, variables: UpdateDataFlowMutationVariables) => void;
};

export function useUpdateDataFlowMutation(options: UseUpdateDataFlowMutationOptions = {}) {
  const {
    showSuccessToast = true,
    showErrorToast = true,
    onSuccess,
    onError,
  } = options;

  return useMutation<UpdateDataFlowResponse, Error, UpdateDataFlowMutationVariables>({
    mutationFn: ({ id, payload }) => updateDataFlow(id, payload),
    onSuccess: (data, variables) => {
      if (showSuccessToast) {
        toast.success(data.message || 'Data flow updated successfully.');
      }
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      if (showErrorToast) {
        toast.error(error.message || 'Failed to update data flow.');
      }
      onError?.(error, variables);
    },
  });
}
